import { useEffect, useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth, type Permissions } from '../lib/auth';
import { formatDateTime } from '../lib/format';
import PageHeader from '../components/PageHeader';
import { useConfirm } from '../components/ConfirmDialog';

type AdminRow = {
  user_id: string;
  email: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  is_super_admin: boolean;
  can_manage_home: boolean;
  can_manage_community: boolean;
  can_manage_volunteering: boolean;
  can_view_volunteering: boolean;
  can_approve_volunteering_coordinator: boolean;
  can_approve_volunteering_hr: boolean;
  can_manage_donations: boolean;
  can_manage_payment_methods: boolean;
  can_manage_chat: boolean;
};

// Presence heuristic. Supabase Auth stamps `last_sign_in_at` on
// every session start, so the freshest sign-in inside the last 5
// minutes is the closest proxy the auth schema exposes to "online
// now" without adding a heartbeat channel.
function presenceOf(lastSignIn: string | null): {
  label: string;
  dot: string;
  tone: 'online' | 'today' | 'idle' | 'never';
} {
  if (!lastSignIn) return { label: 'ما دخلش قبل كده', dot: '#98a3ac', tone: 'never' };
  const diffMin = (Date.now() - new Date(lastSignIn).getTime()) / 60000;
  if (diffMin < 5) return { label: 'نشط دلوقتي', dot: '#1f9d55', tone: 'online' };
  if (diffMin < 60) return { label: `آخر نشاط ${Math.round(diffMin)}د`, dot: '#3b8f68', tone: 'today' };
  if (diffMin < 60 * 24) return { label: `آخر نشاط ${Math.round(diffMin / 60)}س`, dot: '#c68a1a', tone: 'today' };
  return { label: 'غير متصل', dot: '#98a3ac', tone: 'idle' };
}

const emptyPerms: Permissions = {
  is_super_admin: false,
  can_manage_home: false,
  can_manage_community: false,
  can_manage_volunteering: false,
  can_view_volunteering: false,
  can_approve_volunteering_coordinator: false,
  can_approve_volunteering_hr: false,
  can_manage_donations: false,
  can_manage_payment_methods: false,
  can_manage_chat: false,
};

export default function TeamPage() {
  const { session } = useAuth();
  const [rows, setRows] = useState<AdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<AdminRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const confirm = useConfirm();
  const [email, setEmail] = useState('');
  const [perms, setPerms] = useState<Permissions>(emptyPerms);

  useEffect(() => {
    void load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase.rpc('list_app_admins_with_activity');
    if (err) setError(err.message);
    else setRows((data ?? []) as AdminRow[]);
    setLoading(false);
  };

  // Auto-refresh once a minute so the "نشط دلوقتي / منذ X دقيقة" pill
  // stays fresh while the tab is open. Silent — no spinner.
  useEffect(() => {
    const id = setInterval(() => {
      void load();
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  const callFunction = async (
    body: Record<string, unknown>,
  ): Promise<{ ok: boolean; error?: string }> => {
    if (!session) return { ok: false, error: 'مش مسجل دخول' };
    const { data, error: err } = await supabase.functions.invoke<{
      ok?: boolean;
      error?: string;
    }>('admin-users', { body });
    if (err) return { ok: false, error: err.message };
    if (!data?.ok) return { ok: false, error: data?.error ?? 'حصل خطأ غير متوقع' };
    return { ok: true };
  };

  const startInvite = () => {
    setInviting(true);
    setEditing(null);
    setEmail('');
    setPerms(emptyPerms);
  };

  const startEdit = (row: AdminRow) => {
    setEditing(row);
    setInviting(false);
    setPerms({
      is_super_admin: row.is_super_admin,
      can_manage_home: row.can_manage_home,
      can_manage_community: row.can_manage_community,
      can_manage_volunteering: row.can_manage_volunteering,
      can_view_volunteering: row.can_view_volunteering,
      can_approve_volunteering_coordinator: row.can_approve_volunteering_coordinator,
      can_approve_volunteering_hr: row.can_approve_volunteering_hr,
      can_manage_donations: row.can_manage_donations,
      can_manage_payment_methods: row.can_manage_payment_methods,
      can_manage_chat: row.can_manage_chat,
    });
  };

  const closeModal = () => {
    setInviting(false);
    setEditing(null);
    setError(null);
  };

  const invite = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const result = await callFunction({ action: 'invite', email, permissions: perms });
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'حصل خطأ');
      return;
    }
    setNotice(`دعوة اتبعتت لـ ${email} — لازم يفتح اللينك من إيميله ويحدد كلمة السر.`);
    closeModal();
    await load();
  };

  // Send a password-reset link to any admin — for the "forgot password"
  // + "admin resets a peer's password" flows. Uses the caller's session,
  // so RLS on the anon path lands the email at Supabase Auth without
  // needing the admin-users function to relay it.
  const sendReset = async (row: AdminRow) => {
    if (!row.email) return;
    if (!(await confirm({
      title: 'ابعت لينك إعادة تعيين كلمة السر؟',
      body: `الأدمن ${row.email} هيوصله إيميل بلينك جديد لتغيير كلمة السر.`,
      confirmText: 'ابعت',
    }))) return;
    setError(null);
    setNotice(null);
    const redirectTo = `${window.location.origin}/set-password`;
    const { error: err } = await supabase.auth.resetPasswordForEmail(row.email, { redirectTo });
    if (err) {
      setError(err.message);
      return;
    }
    setNotice(`لينك تغيير كلمة السر اتبعت لـ ${row.email}.`);
  };

  const updatePerms = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setBusy(true);
    setError(null);
    const result = await callFunction({
      action: 'update',
      user_id: editing.user_id,
      permissions: perms,
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'حصل خطأ');
      return;
    }
    closeModal();
    await load();
  };

  const revoke = async (row: AdminRow) => {
    if (!(await confirm({
      title: 'إزالة صلاحيات الأدمن؟',
      body: `الأدمن ${row.email ?? row.user_id} مش هيقدر يدخل بعدها. الحساب هيفضل موجود ف Auth للأودت بس، والجلسات الحالية بتُلغى.`,
      confirmText: 'إزالة',
      tone: 'danger',
    }))) return;
    setError(null);
    const result = await callFunction({ action: 'revoke', user_id: row.user_id });
    if (!result.ok) {
      setError(result.error ?? 'حصل خطأ');
      return;
    }
    await load();
  };

  const permsSummary = (row: AdminRow) => {
    if (row.is_super_admin) return 'كل الصلاحيات (Super Admin)';
    const parts: string[] = [];
    if (row.can_manage_home) parts.push('الرئيسية');
    if (row.can_manage_community) parts.push('مجتمع مرسال');
    if (row.can_manage_volunteering) {
      parts.push('التطوع');
    } else {
      const volApprovals: string[] = [];
      if (row.can_approve_volunteering_coordinator) volApprovals.push('موافقة مسؤول');
      if (row.can_approve_volunteering_hr) volApprovals.push('موافقة HR');
      if (volApprovals.length > 0) parts.push(`التطوع (${volApprovals.join(' + ')})`);
      else if (row.can_view_volunteering) parts.push('التطوع (عرض فقط)');
    }
    if (row.can_manage_donations) parts.push('التبرعات');
    if (row.can_manage_payment_methods) parts.push('طرق التبرع');
    if (row.can_manage_chat) parts.push('الدردشة');
    return parts.length ? parts.join(' + ') : '—';
  };

  return (
    <>
      <PageHeader
        eyebrow="إدارة المستخدمين"
        title="فريق الإدارة"
        subtitle="كل أدمن معاه صلاحيات مختلفة — تقدر تعطي حق قسم واحد بس أو كل الأقسام"
        actions={
          <button className="btn" onClick={startInvite}>
            + إضافة أدمن
          </button>
        }
      />

      {error && <div className="error-inline">{error}</div>}
      {notice && !error && (
        <div
          className="error-inline"
          style={{ background: 'rgba(31,157,85,0.12)', color: 'var(--teal-dark)', border: '1px solid rgba(31,157,85,0.35)' }}
        >
          {notice}
        </div>
      )}

      {loading ? (
        <div className="empty">جاري التحميل...</div>
      ) : rows.length === 0 ? (
        <div className="card empty">مفيش أدمن — ده مفروض ميحصلش أبدًا.</div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>الإيميل</th>
              <th>الحالة</th>
              <th>آخر دخول</th>
              <th>الصلاحيات</th>
              <th>تاريخ الإضافة</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isSelf = row.user_id === session?.user.id;
              // The current viewer is by definition online right now —
              // the `last_sign_in_at` heuristic only updates on a fresh
              // password/OAuth sign-in, so a long-lived valid session
              // would otherwise render its own owner as "غير متصل" for
              // days after signing in once. Bypass the heuristic for
              // the current user; other rows keep the last-sign-in
              // proxy.
              const presence = isSelf
                ? { label: 'نشط دلوقتي', dot: '#1f9d55', tone: 'online' as const }
                : presenceOf(row.last_sign_in_at);
              return (
                <tr key={row.user_id}>
                  <td>
                    {row.email ?? row.user_id}
                    {isSelf && (
                      <span className="badge badge-success" style={{ marginRight: 8 }}>
                        أنت
                      </span>
                    )}
                    {row.is_super_admin && (
                      <span className="badge badge-pending" style={{ marginRight: 8 }}>
                        Super Admin
                      </span>
                    )}
                  </td>
                  <td>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '4px 10px',
                        borderRadius: 999,
                        background: presence.tone === 'online' ? 'rgba(31,157,85,0.10)' : '#f2f4f7',
                        color: presence.tone === 'online' ? '#1f9d55' : 'var(--text-secondary)',
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: presence.dot,
                          boxShadow: presence.tone === 'online' ? '0 0 0 3px rgba(31,157,85,0.20)' : 'none',
                        }}
                      />
                      {presence.label}
                    </span>
                  </td>
                  <td>{row.last_sign_in_at ? formatDateTime(row.last_sign_in_at) : '—'}</td>
                  <td>{permsSummary(row)}</td>
                  <td>{formatDateTime(row.created_at)}</td>
                  <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-small btn-secondary"
                      onClick={() => startEdit(row)}
                    >
                      تعديل
                    </button>
                    <button
                      className="btn btn-small btn-secondary"
                      onClick={() => void sendReset(row)}
                      disabled={!row.email}
                      title={!row.email ? 'مفيش إيميل' : 'ابعت لينك تغيير كلمة السر'}
                    >
                      إعادة كلمة السر
                    </button>
                    <button
                      className="btn btn-small btn-danger"
                      onClick={() => void revoke(row)}
                      disabled={isSelf}
                      title={isSelf ? 'مينفعش تشيل نفسك' : ''}
                    >
                      إزالة
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {(inviting || editing) && (
        <div className="modal-backdrop" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {inviting
                  ? 'إضافة أدمن جديد'
                  : `تعديل صلاحيات: ${editing?.email ?? ''}`}
              </h2>
              <button className="btn btn-small btn-secondary" onClick={closeModal}>
                إغلاق
              </button>
            </div>

            <form onSubmit={inviting ? invite : updatePerms}>
              {inviting && (
                <>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 0 }}>
                    هيوصل للأدمن الجديد إيميل فيه رابط دعوة — يفتحه ويحدد كلمة السر بنفسه.
                    مش هتحتاج تبعت الباسورد ولا اليوزر يشاركه معاك.
                  </p>
                  <div className="field">
                    <label>الإيميل</label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@example.com"
                    />
                  </div>
                </>
              )}

              <div className="field">
                <label>الصلاحيات</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/*
                    U-ADM-03 · pre-launch audit hardening.
                    A super admin editing their OWN row was previously
                    able to uncheck the `is_super_admin` box and save,
                    demoting themselves to a nothing-role. If they
                    happened to be the last super admin, the org lost
                    the whole /team page. Disable the checkbox in that
                    exact case (self + currently super) and explain in
                    plain Arabic. The server-side protect_super_admin()
                    trigger still enforces "at least one super" as the
                    last line of defence.
                  */}
                  {(() => {
                    const isSelfSuperEdit =
                      !inviting &&
                      editing?.user_id === session?.user.id &&
                      editing?.is_super_admin;
                    return (
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 10,
                          padding: 10,
                          border: '1px solid var(--border)',
                          borderRadius: 10,
                          background: perms.is_super_admin ? 'rgba(254, 200, 48, 0.08)' : '#fff',
                          opacity: isSelfSuperEdit ? 0.7 : 1,
                        }}
                        title={
                          isSelfSuperEdit
                            ? 'مينفعش تشيل الـ Super Admin من نفسك — اطلب من super admin تاني يعمله'
                            : ''
                        }
                      >
                        <input
                          type="checkbox"
                          checked={perms.is_super_admin}
                          disabled={isSelfSuperEdit}
                          onChange={(e) =>
                            setPerms({ ...perms, is_super_admin: e.target.checked })
                          }
                          style={{ marginTop: 3 }}
                        />
                        <div>
                          <strong>Super Admin</strong>
                          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                            صلاحيات كاملة على كل الأقسام + إدارة فريق الأدمن
                          </div>
                          {isSelfSuperEdit && (
                            <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>
                              مينفعش تشيل الـ Super Admin من نفسك — اطلب super admin تاني يعمله.
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })()}

                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: 10,
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      opacity: perms.is_super_admin ? 0.55 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={perms.is_super_admin || perms.can_manage_home}
                      disabled={perms.is_super_admin}
                      onChange={(e) =>
                        setPerms({ ...perms, can_manage_home: e.target.checked })
                      }
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <strong>الرئيسية (التطبيق)</strong>
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        نشر الأخبار والحالات العاجلة اللي تظهر في شاشة الرئيسية بالتطبيق
                      </div>
                    </div>
                  </label>

                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: 10,
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      opacity: perms.is_super_admin ? 0.55 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={perms.is_super_admin || perms.can_manage_community}
                      disabled={perms.is_super_admin}
                      onChange={(e) =>
                        setPerms({ ...perms, can_manage_community: e.target.checked })
                      }
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <strong>مجتمع مرسال</strong>
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        إدارة الشركاء والعروض + رؤية تقارير مجتمع مرسال
                      </div>
                    </div>
                  </label>

                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: 10,
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      opacity: perms.is_super_admin ? 0.55 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={perms.is_super_admin || perms.can_manage_volunteering}
                      disabled={perms.is_super_admin}
                      onChange={(e) =>
                        setPerms({ ...perms, can_manage_volunteering: e.target.checked })
                      }
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <strong>التطوع</strong>
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        إدارة الفرص + رؤية المتقدمين + تقارير التطوع
                      </div>
                    </div>
                  </label>

                  {/* View-only slot — grants read access to the
                      applications pipeline without any edit path.
                      Auto-implied when can_manage_volunteering is on. */}
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: 10,
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      opacity: perms.is_super_admin || perms.can_manage_volunteering ? 0.55 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={
                        perms.is_super_admin ||
                        perms.can_manage_volunteering ||
                        perms.can_view_volunteering
                      }
                      disabled={perms.is_super_admin || perms.can_manage_volunteering}
                      onChange={(e) =>
                        setPerms({ ...perms, can_view_volunteering: e.target.checked })
                      }
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <strong>التطوع (عرض فقط)</strong>
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        قراءة قايمة المتطوعين + الحالات، بدون تعديل أو قرار HR
                      </div>
                    </div>
                  </label>

                  {/* Granular approval permissions — each opens the
                      corresponding "موافقة مسؤول المتطوعين" or
                      "موافقة الـ HR" tab and only lets its owner move
                      rows through THAT stage of the pipeline. A trigger
                      on volunteers.application_status enforces this on
                      the DB. Auto-implied by can_manage_volunteering. */}
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: 10,
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      opacity: perms.is_super_admin || perms.can_manage_volunteering ? 0.55 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={
                        perms.is_super_admin ||
                        perms.can_manage_volunteering ||
                        perms.can_approve_volunteering_coordinator
                      }
                      disabled={perms.is_super_admin || perms.can_manage_volunteering}
                      onChange={(e) =>
                        setPerms({ ...perms, can_approve_volunteering_coordinator: e.target.checked })
                      }
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <strong>موافقة مسؤول المتطوعين</strong>
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        الموافقة على الطلبات في مرحلة "قيد المراجعة" وتحويلها لـ HR (أو رفضها)
                      </div>
                    </div>
                  </label>

                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: 10,
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      opacity: perms.is_super_admin || perms.can_manage_volunteering ? 0.55 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={
                        perms.is_super_admin ||
                        perms.can_manage_volunteering ||
                        perms.can_approve_volunteering_hr
                      }
                      disabled={perms.is_super_admin || perms.can_manage_volunteering}
                      onChange={(e) =>
                        setPerms({ ...perms, can_approve_volunteering_hr: e.target.checked })
                      }
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <strong>موافقة الـ HR</strong>
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        القرار النهائي على الطلبات اللي عند HR — قبول أو رفض
                      </div>
                    </div>
                  </label>

                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: 10,
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      opacity: perms.is_super_admin ? 0.55 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={perms.is_super_admin || perms.can_manage_donations}
                      disabled={perms.is_super_admin}
                      onChange={(e) =>
                        setPerms({ ...perms, can_manage_donations: e.target.checked })
                      }
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <strong>التبرعات</strong>
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        إدارة الحملات + رؤية سجل التبرعات + تقارير التبرعات
                      </div>
                    </div>
                  </label>

                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: 10,
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      opacity: perms.is_super_admin ? 0.55 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={perms.is_super_admin || perms.can_manage_payment_methods}
                      disabled={perms.is_super_admin}
                      onChange={(e) =>
                        setPerms({ ...perms, can_manage_payment_methods: e.target.checked })
                      }
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <strong>طرق التبرع</strong>
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        كل طرق التبرع اللي بتظهر ف الموبايل: مندوب + إلكتروني + بنوك
                      </div>
                    </div>
                  </label>

                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: 10,
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      opacity: perms.is_super_admin ? 0.55 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={perms.is_super_admin || perms.can_manage_chat}
                      disabled={perms.is_super_admin}
                      onChange={(e) =>
                        setPerms({ ...perms, can_manage_chat: e.target.checked })
                      }
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <strong>الدردشة (خدمة العملاء)</strong>
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        رول محدود: يستقبل رسائل الزوار من التطبيق ويرد عليها ويقفل الاستفسار — من غير باقي صلاحيات مجتمع مرسال
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  إلغاء
                </button>
                <button type="submit" className="btn" disabled={busy}>
                  {busy
                    ? 'جاري الإرسال...'
                    : inviting
                      ? 'إرسال الدعوة'
                      : 'حفظ التغييرات'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
